import {Component, OnDestroy, OnInit} from '@angular/core';
import {TenantService} from '@twpub/core/services';
import {NGXLogger} from 'ngx-logger';
import {SynchronizeDictionariesTaskStatus} from '@twpub/core/models/synchronize-dictionaries-task-status';
import {TenantConfiguration} from '@twpub/core/models/tenant-configuration';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatOption, MatSelect} from '@angular/material/select';

import {PrettyDatePipe} from '@twpub/shared/pipes/pretty-date.pipe';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';

@Component({
  selector: 'pub-scheduled-sync-settings',
  templateUrl: './scheduled-sync-settings.component.html',
  styleUrls: ['./scheduled-sync-settings.component.scss'],
  standalone: true,
  imports: [MatCard, MatCardHeader, MatCardTitle, MatCardContent, MatFormField, MatLabel, MatSelect, MatOption, PrettyDatePipe, MatButton, MatProgressSpinner]
})
export class ScheduledSyncSettingsComponent implements OnInit, OnDestroy {
  updateIntervals = [
    {value: 0, viewValue: 'No automatic updates'},
    {value: 1, viewValue: 'Every hour'},
    {value: 2, viewValue: 'Every 2 hours'},
    {value: 4, viewValue: 'Every 4 hours'},
    {value: 8, viewValue: 'Every 8 hours'},
    {value: 12, viewValue: 'Every 12 hours'},
    {value: 24, viewValue: 'Every day'},
    {value: 48, viewValue: 'Every 2 days'},
    {value: 168, viewValue: 'Once a week'}
  ];

  selectedInterval: number = 0;
  scheduledStatus?: SynchronizeDictionariesTaskStatus;
  syncTriggered = false;

  private tenantConfiguration?: TenantConfiguration;

  get isSyncing(): boolean {
    return this.syncTriggered || (this.scheduledStatus?.running ?? false);
  }

  private pollingInterval?: ReturnType<typeof setInterval>;

  constructor(private logger: NGXLogger, private tenantService: TenantService) {
  }

  ngOnInit(): void {
    this.tenantService.getCurrentTenant().subscribe({
      next: (config) => {
        this.tenantConfiguration = config;
        this.selectedInterval = config.scheduledSyncHours || 0;
      }
    });
    this.getScheduledStatus();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  onSelectionChange(): void {
    if (this.tenantConfiguration) {
      this.tenantConfiguration.scheduledSyncHours = this.selectedInterval;
      this.tenantService.updateConfiguration(this.tenantConfiguration).subscribe({next: () => this.getScheduledStatus()});
    }
  }

  runSyncNow(): void {
    this.syncTriggered = true;
    this.tenantService.runSyncNow().subscribe({
      next: () => {
        this.startPolling();
      },
      error: (err) => {
        this.logger.error('Failed to start sync', err);
        this.syncTriggered = false;
      }
    });
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollingInterval = setInterval(() => {
      this.getScheduledStatus();
    }, 2000);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = undefined;
    }
  }

  private getScheduledStatus(): void {
    this.tenantService.getScheduledSyncStatus().subscribe({
      next: (status) => {
        this.scheduledStatus = status;
        if (status.running) {
          if (!this.pollingInterval) {
            this.startPolling();
          }
        } else {
          this.syncTriggered = false;
          this.stopPolling();
        }
      },
      error: (err) => {
        this.logger.error('Failed to get sync status', err);
        this.syncTriggered = false;
        this.stopPolling();
      }
    });
  }
}
